import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatFileSize } from '@wundo/shared/fileSize.ts';
import { Avatar, ImageUploadDialog } from '@wundo/ui';
import { nameToAbbreviate } from './nameToAbbreviate.ts';
import type { CurrentUser } from '@wundo/client/auth.ts';
import {
  MAX_PROFILE_IMAGE_BYTES,
  PROFILE_IMAGE_MIME_TYPES,
  removeProfileImage,
  uploadProfileImage,
} from '@/lib/profileImage.ts';
import { m } from '@/paraglide/messages.js';

export interface ProfilePhotoDialogProps {
  user: CurrentUser;
  onClose: () => void;
}

/**
 * Both routes answer 204, so the user is fetched again instead: that puts the
 * new picture on screen and carries it to every other page reading the record.
 */
export function ProfilePhotoDialog({ user, onClose }: ProfilePhotoDialogProps) {
  const queryClient = useQueryClient();
  const size = formatFileSize(MAX_PROFILE_IMAGE_BYTES);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['currentUser'] });

  const upload = useMutation({
    mutationFn: (file: File) => uploadProfileImage(user.id, file),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: () => removeProfileImage(user.id),
    onSuccess: refresh,
  });

  return (
    <ImageUploadDialog
      title={m.settings_photo()}
      closeLabel={m.settings_photo_close()}
      preview={
        <Avatar
          className='size-32 text-xl'
          src={user.profile_image_url}
          name={nameToAbbreviate(user)}
          seed={user.email ?? user.id}
        />
      }
      hint={m.settings_photo_desc({ size })}
      acceptTypes={PROFILE_IMAGE_MIME_TYPES}
      maxBytes={MAX_PROFILE_IMAGE_BYTES}
      tooLargeMessage={m.settings_photo_too_large({ size })}
      wrongTypeMessage={m.settings_photo_wrong_type()}
      failedMessage={upload.isError || remove.isError ? m.settings_photo_failed() : null}
      uploadLabel={
        user.profile_image_url ? m.settings_photo_change() : m.settings_photo_add()
      }
      uploading={upload.isPending}
      onUpload={(file) => upload.mutate(file)}
      removeLabel={m.settings_photo_remove()}
      removing={remove.isPending}
      onRemove={user.profile_image_url ? () => remove.mutate() : undefined}
      onClose={onClose}
    />
  );
}
